import { defineStore } from "pinia";
import { ref } from "vue";

import {
  fetchBillingUsage,
  getBillingEntitlement,
  getBillingHistory,
  openCustomerPortal,
  startProCheckout,
  type BillingEntitlement,
  type BillingPayment,
  type BillingUsageSnapshot,
} from "@/api/billing";
import { navigateToExternalUrl } from "@/utils/externalUrl";

export type { BillingUsageMetric, BillingUsageMetricKey } from "@/api/billing";

export type BillingPhase = "error" | "idle" | "loading" | "success";
export type BillingUsagePhase = "error" | "idle" | "loading" | "refreshing" | "success";

export const useBillingStore = defineStore("billing", () => {
  const entitlement = ref<BillingEntitlement | null>(null);
  const payments = ref<BillingPayment[]>([]);
  const error = ref("");
  const loading = ref(false);
  const phase = ref<BillingPhase>("idle");
  const usage = ref<BillingUsageSnapshot | null>(null);
  const usageError = ref("");
  const usagePhase = ref<BillingUsagePhase>("idle");

  let sessionGeneration = 0;
  let refreshGeneration = 0;
  let actionGeneration = 0;
  let usageGeneration = 0;

  function ownsRefresh(requestGeneration: number, session: number): boolean {
    return requestGeneration === refreshGeneration && session === sessionGeneration;
  }

  function ownsAction(requestGeneration: number, session: number): boolean {
    return requestGeneration === actionGeneration && session === sessionGeneration;
  }

  function ownsUsage(requestGeneration: number, session: number): boolean {
    return requestGeneration === usageGeneration && session === sessionGeneration;
  }

  async function refresh(): Promise<void> {
    const requestGeneration = ++refreshGeneration;
    const session = sessionGeneration;
    loading.value = true;
    error.value = "";
    phase.value = "loading";
    try {
      const [nextEntitlement, nextPayments] = await Promise.all([
        getBillingEntitlement(),
        getBillingHistory(),
      ]);
      if (!ownsRefresh(requestGeneration, session)) return;
      entitlement.value = nextEntitlement;
      payments.value = nextPayments;
      phase.value = "success";
    } catch (caught) {
      if (!ownsRefresh(requestGeneration, session)) return;
      error.value = caught instanceof Error ? caught.message : "Billing status is unavailable.";
      phase.value = "error";
    } finally {
      if (ownsRefresh(requestGeneration, session)) loading.value = false;
    }
  }

  async function refreshUsage(): Promise<void> {
    const requestGeneration = ++usageGeneration;
    const session = sessionGeneration;
    usageError.value = "";
    usagePhase.value = usage.value ? "refreshing" : "loading";
    try {
      const snapshot = await fetchBillingUsage();
      if (!ownsUsage(requestGeneration, session)) return;
      usage.value = snapshot;
      usagePhase.value = "success";
    } catch (caught) {
      if (!ownsUsage(requestGeneration, session)) return;
      usageError.value = caught instanceof Error
        ? caught.message
        : "Billing usage is unavailable.";
      usagePhase.value = "error";
    }
  }

  async function checkout(): Promise<void> {
    const requestGeneration = ++actionGeneration;
    const session = sessionGeneration;
    loading.value = true;
    error.value = "";
    try {
      const url = await startProCheckout();
      if (!ownsAction(requestGeneration, session)) return;
      navigateToExternalUrl(url);
    } catch (caught) {
      if (!ownsAction(requestGeneration, session)) return;
      error.value = caught instanceof Error ? caught.message : "Checkout is unavailable.";
      loading.value = false;
    }
  }

  async function portal(): Promise<void> {
    const requestGeneration = ++actionGeneration;
    const session = sessionGeneration;
    loading.value = true;
    error.value = "";
    try {
      const url = await openCustomerPortal();
      if (!ownsAction(requestGeneration, session)) return;
      navigateToExternalUrl(url);
    } catch (caught) {
      if (!ownsAction(requestGeneration, session)) return;
      error.value = caught instanceof Error ? caught.message : "Billing portal is unavailable.";
      loading.value = false;
    }
  }

  function reset(): void {
    sessionGeneration += 1;
    refreshGeneration += 1;
    actionGeneration += 1;
    usageGeneration += 1;
    entitlement.value = null;
    payments.value = [];
    error.value = "";
    loading.value = false;
    phase.value = "idle";
    usage.value = null;
    usageError.value = "";
    usagePhase.value = "idle";
  }

  return {
    checkout,
    entitlement,
    error,
    loading,
    payments,
    phase,
    portal,
    refresh,
    refreshUsage,
    reset,
    usage,
    usageError,
    usagePhase,
  };
});
